import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { 
  LayoutDashboard, 
  FileText, 
  User, 
  LogOut, 
  Menu, 
  X,
  Plus,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  AlertTriangle,
  MessageCircle,
  GraduationCap,
  Users,
  Home,
  Upload,
  Loader2,
  Calendar,
  BookOpen,
  Trophy,
  Crown,
  Medal
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const WHATSAPP_NUMBER = '447949785005';

export default function DashboardPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAuthenticated, loading: authLoading } = useAuth();
  
  const [activeTab, setActiveTab] = useState('overview');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [summary, setSummary] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [linkedStudent, setLinkedStudent] = useState(null);

  // Check if we have user from callback
  const passedUser = location.state?.user;

  useEffect(() => {
    // If not authenticated and no passed user, redirect to login
    if (!authLoading && !isAuthenticated && !passedUser) {
      navigate('/login', { replace: true });
    }
  }, [authLoading, isAuthenticated, passedUser, navigate]);

  useEffect(() => {
    if (isAuthenticated || passedUser) {
      fetchDashboardData();
    }
  }, [isAuthenticated, passedUser]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // Fetch summary
      const summaryRes = await fetch(`${API_URL}/api/dashboard/rag-reports/summary`, {
        credentials: 'include',
      });
      if (summaryRes.ok) {
        const summaryData = await summaryRes.json();
        setSummary(summaryData);
      }

      // Fetch all reports
      const reportsRes = await fetch(`${API_URL}/api/dashboard/rag-reports`, {
        credentials: 'include',
      });
      if (reportsRes.ok) {
        const reportsData = await reportsRes.json();
        setReports(reportsData.reports);
      }

      // For parents, fetch linked student
      const currentUser = user || passedUser;
      if (currentUser?.role === 'parent') {
        const studentRes = await fetch(`${API_URL}/api/dashboard/parent/student`, {
          credentials: 'include',
        });
        if (studentRes.ok) {
          const studentData = await studentRes.json();
          setLinkedStudent(studentData.student);
        }
      }
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const openWhatsApp = () => {
    const currentUser = user || passedUser;
    const message = `Hi Dr. Michael, I'm ${currentUser?.name} from the Abba Academy portal.`;
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`, '_blank');
  };

  const currentUser = user || passedUser;

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#FFFCF5] flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-[#C5A059] animate-spin" />
      </div>
    );
  }

  if (!currentUser) {
    return null;
  }

  const isStudent = currentUser.role === 'student';
  const isParent = currentUser.role === 'parent';

  const navItems = [
    { id: 'overview', label: 'Overview', icon: LayoutDashboard },
    { id: 'reports', label: 'RAG Reports', icon: FileText },
    ...(isStudent ? [{ id: 'upload', label: 'Upload Report', icon: Upload }] : []),
    { id: 'leaderboard', label: 'Leaderboard', icon: Trophy },
    { id: 'profile', label: 'My Profile', icon: User },
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-[#E2E8F0]">
        <div className="flex items-center justify-between p-4">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 hover:bg-[#F1F5F9] rounded-lg"
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-6 h-6 text-[#0F172A]" />
          </button>
          <h1 className="font-['Playfair_Display'] text-xl font-bold text-[#0F172A]">
            Abba Academy
          </h1>
          <div className="w-10" />
        </div>
      </div>

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 z-50 h-full w-72 bg-[#0F172A] transform transition-transform duration-300
        lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        {/* Close button (mobile) */}
        <button
          onClick={() => setSidebarOpen(false)}
          className="lg:hidden absolute top-4 right-4 p-2 text-white/60 hover:text-white"
        >
          <X className="w-6 h-6" />
        </button>

        {/* Logo */}
        <div className="p-6 border-b border-white/10">
          <h1 className="font-['Playfair_Display'] text-2xl font-bold text-white">
            Abba Academy
          </h1>
          <p className="text-white/60 text-sm mt-1">
            {isStudent ? 'Student Portal' : 'Parent Portal'}
          </p>
        </div>

        {/* User Info */}
        <div className="p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-[#C5A059]/20 flex items-center justify-center">
              {currentUser.picture ? (
                <img src={currentUser.picture} alt="" className="w-12 h-12 rounded-full" />
              ) : isStudent ? (
                <GraduationCap className="w-6 h-6 text-[#C5A059]" />
              ) : (
                <Users className="w-6 h-6 text-[#C5A059]" />
              )}
            </div>
            <div>
              <p className="text-white font-medium truncate max-w-[160px]">{currentUser.name}</p>
              <p className="text-white/60 text-sm truncate max-w-[160px]">{currentUser.email}</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-2">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => {
                setActiveTab(item.id);
                setSidebarOpen(false);
              }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                activeTab === item.id
                  ? 'bg-[#C5A059] text-white'
                  : 'text-white/70 hover:bg-white/10 hover:text-white'
              }`}
              data-testid={`nav-${item.id}`}
            >
              <item.icon className="w-5 h-5" />
              <span className="font-medium">{item.label}</span>
            </button>
          ))}
        </nav>

        {/* WhatsApp Button */}
        <div className="absolute bottom-20 left-4 right-4">
          <Button
            onClick={openWhatsApp}
            className="w-full bg-[#25D366] hover:bg-[#20BD5A] text-white"
            data-testid="whatsapp-btn"
          >
            <MessageCircle className="w-5 h-5 mr-2" />
            Message Dr. Michael
          </Button>
        </div>

        {/* Logout & Home */}
        <div className="absolute bottom-4 left-4 right-4 space-y-2">
          <Button
            onClick={() => navigate('/')}
            variant="ghost"
            className="w-full text-white/70 hover:text-white hover:bg-white/10"
            data-testid="home-btn"
          >
            <Home className="w-5 h-5 mr-2" />
            Back to Website
          </Button>
          <Button
            onClick={handleLogout}
            variant="ghost"
            className="w-full text-white/70 hover:text-white hover:bg-white/10"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5 mr-2" />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-black/50"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="lg:ml-72 pt-20 lg:pt-0 min-h-screen">
        <div className="p-6 lg:p-8">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <OverviewTab 
              summary={summary} 
              loading={loading} 
              user={currentUser}
              linkedStudent={linkedStudent}
              onUpload={() => setActiveTab('upload')}
            />
          )}

          {/* Reports Tab */}
          {activeTab === 'reports' && (
            <ReportsTab 
              reports={reports} 
              loading={loading}
              onRefresh={fetchDashboardData}
            />
          )}

          {/* Upload Tab (Students Only) */}
          {activeTab === 'upload' && isStudent && (
            <UploadTab 
              onSuccess={() => {
                fetchDashboardData();
                setActiveTab('reports');
              }}
            />
          )}

          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <ProfileTab 
              user={currentUser}
              linkedStudent={linkedStudent}
              onRefresh={fetchDashboardData}
            />
          )}

          {/* Leaderboard Tab */}
          {activeTab === 'leaderboard' && (
            <LeaderboardTab currentUserId={currentUser?.user_id} />
          )}
        </div>
      </main>
    </div>
  );
}

// ============ OVERVIEW TAB ============
function OverviewTab({ summary, loading, user, linkedStudent, onUpload }) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 text-[#C5A059] animate-spin" />
      </div>
    );
  }

  const isParent = user?.role === 'parent';
  const studentName = isParent ? linkedStudent?.name : user?.name;

  return (
    <div className="space-y-8">
      {/* Welcome Header */}
      <div>
        <h1 className="font-['Playfair_Display'] text-3xl font-bold text-[#0F172A]">
          Welcome back, {user?.name?.split(' ')[0]}!
        </h1>
        <p className="text-[#64748B] mt-2">
          {isParent 
            ? `Viewing ${linkedStudent ? linkedStudent.name + "'s" : 'your child\'s'} progress`
            : 'Track your academic progress and upload RAG reports'
          }
        </p>
      </div>

      {/* Parent Notice (if no linked student) */}
      {isParent && !linkedStudent && (
        <div className="bg-[#FFFCF5] border border-[#C5A059]/30 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <AlertTriangle className="w-6 h-6 text-[#C5A059] flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-[#0F172A]">Link Your Child's Account</h3>
              <p className="text-[#0F172A]/80 text-sm mt-1">
                To view your child's progress, please link their account in your profile settings.
                They must register as a student first.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatsCard
          icon={FileText}
          label="Total Reports"
          value={summary?.total_reports || 0}
          color="text-[#0F172A]"
          bgColor="bg-[#F1F5F9]"
        />
        <StatsCard
          icon={CheckCircle}
          label="Green (On Track)"
          value={summary?.green_count || 0}
          color="text-green-600"
          bgColor="bg-green-50"
        />
        <StatsCard
          icon={AlertTriangle}
          label="Amber (Needs Work)"
          value={summary?.amber_count || 0}
          color="text-amber-600"
          bgColor="bg-amber-50"
        />
        <StatsCard
          icon={AlertCircle}
          label="Red (Focus Area)"
          value={summary?.red_count || 0}
          color="text-red-600"
          bgColor="bg-red-50"
        />
      </div>

      {/* Badges Section */}
      {summary?.badges?.earned?.length > 0 && (
        <div className="bg-gradient-to-br from-[#0F172A] to-[#1E293B] rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-['Playfair_Display'] text-xl font-bold text-white">
              Your Achievements
            </h2>
            <span className="text-white/60 text-sm">
              {summary.badges.earned_count} of {summary.badges.total_available} badges
            </span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {summary.badges.earned.map((badge) => (
              <BadgeCard key={badge.id} badge={badge} />
            ))}
          </div>
        </div>
      )}

      {/* Subject Breakdown */}
      {summary?.subjects && Object.keys(summary.subjects).length > 0 && (
        <div className="bg-white rounded-xl border border-[#E2E8F0] p-6">
          <h2 className="font-['Playfair_Display'] text-xl font-bold text-[#0F172A] mb-6">
            Progress by Subject
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(summary.subjects).map(([subject, data]) => (
              <SubjectCard key={subject} subject={subject} data={data} />
            ))}
          </div>
        </div>
      )}

      {/* Recent Reports */}
      {summary?.recent_reports?.length > 0 && (
        <div className="bg-white rounded-xl border border-[#E2E8F0] p-6">
          <h2 className="font-['Playfair_Display'] text-xl font-bold text-[#0F172A] mb-6">
            Recent Reports
          </h2>
          <div className="space-y-3">
            {summary.recent_reports.slice(0, 3).map((report, idx) => (
              <ReportRow key={idx} report={report} />
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {(!summary?.total_reports || summary.total_reports === 0) && !isParent && (
        <div className="bg-white rounded-xl border border-[#E2E8F0] p-12 text-center">
          <div className="w-16 h-16 bg-[#C5A059]/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Upload className="w-8 h-8 text-[#C5A059]" />
          </div>
          <h3 className="font-['Playfair_Display'] text-xl font-bold text-[#0F172A] mb-2">
            No Reports Yet
          </h3>
          <p className="text-[#64748B] mb-6">
            Start tracking your progress by uploading your first RAG report.
          </p>
          <Button
            onClick={onUpload}
            className="bg-[#C5A059] hover:bg-[#B8934E] text-white"
            data-testid="upload-first-report-btn"
          >
            <Plus className="w-5 h-5 mr-2" />
            Upload Your First Report
          </Button>
        </div>
      )}
    </div>
  );
}

function StatsCard({ icon: Icon, label, value, color, bgColor }) {
  return (
    <div className="bg-white rounded-xl border border-[#E2E8F0] p-5">
      <div className={`w-10 h-10 ${bgColor} rounded-lg flex items-center justify-center mb-3`}>
        <Icon className={`w-5 h-5 ${color}`} />
      </div>
      <p className="text-2xl font-bold text-[#0F172A]">{value}</p>
      <p className="text-sm text-[#64748B]">{label}</p>
    </div>
  );
}

function SubjectCard({ subject, data }) {
  const total = data.total || 0;
  const greenPct = total > 0 ? (data.green / total) * 100 : 0;
  const amberPct = total > 0 ? (data.amber / total) * 100 : 0;
  const redPct = total > 0 ? (data.red / total) * 100 : 0;

  return (
    <div className="p-4 bg-[#F8FAFC] rounded-lg">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-[#0F172A]">{subject}</h3>
        <span className="text-sm text-[#64748B]">{total} reports</span>
      </div>
      <div className="h-2 rounded-full bg-[#E2E8F0] overflow-hidden flex">
        <div className="bg-green-500" style={{ width: `${greenPct}%` }} />
        <div className="bg-amber-500" style={{ width: `${amberPct}%` }} />
        <div className="bg-red-500" style={{ width: `${redPct}%` }} />
      </div>
      <div className="flex justify-between mt-2 text-xs text-[#64748B]">
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-green-500" /> {data.green}
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-amber-500" /> {data.amber}
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-red-500" /> {data.red}
        </span>
      </div>
    </div>
  );
}

function ReportRow({ report }) {
  const statusColors = {
    green: 'bg-green-100 text-green-700',
    amber: 'bg-amber-100 text-amber-700',
    red: 'bg-red-100 text-red-700',
  };

  return (
    <div className="flex items-center justify-between p-4 bg-[#F8FAFC] rounded-lg">
      <div className="flex items-center gap-4">
        <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center border border-[#E2E8F0]">
          <BookOpen className="w-5 h-5 text-[#64748B]" />
        </div>
        <div>
          <p className="font-medium text-[#0F172A]">{report.test_name}</p>
          <p className="text-sm text-[#64748B]">{report.subject}</p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusColors[report.rag_status]}`}>
          {report.rag_status.toUpperCase()}
        </span>
        <span className="text-sm text-[#64748B]">{report.test_date}</span>
      </div>
    </div>
  );
}

function BadgeCard({ badge, isNew = false }) {
  return (
    <div 
      className={`relative p-4 rounded-xl text-center transition-transform hover:scale-105 ${
        isNew ? 'ring-2 ring-[#C5A059] ring-offset-2 ring-offset-[#0F172A]' : ''
      }`}
      style={{ background: `${badge.color}20` }}
    >
      {isNew && (
        <div className="absolute -top-2 -right-2 bg-[#C5A059] text-white text-xs font-bold px-2 py-1 rounded-full">
          NEW!
        </div>
      )}
      <div className="text-3xl mb-2">{badge.emoji}</div>
      <h4 className="text-white font-semibold text-sm mb-1">{badge.name}</h4>
      <p className="text-white/60 text-xs leading-tight">{badge.description}</p>
    </div>
  );
}

// ============ REPORTS TAB ============
function ReportsTab({ reports, loading, onRefresh }) {
  const [filter, setFilter] = useState('all');
  const [subjectFilter, setSubjectFilter] = useState('all');

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 text-[#C5A059] animate-spin" />
      </div>
    );
  }

  const subjects = [...new Set(reports.map(r => r.subject))];
  
  const filteredReports = reports.filter(report => {
    if (filter !== 'all' && report.rag_status !== filter) return false;
    if (subjectFilter !== 'all' && report.subject !== subjectFilter) return false;
    return true;
  });

  const statusColors = {
    green: 'bg-green-100 text-green-700 border-green-200',
    amber: 'bg-amber-100 text-amber-700 border-amber-200',
    red: 'bg-red-100 text-red-700 border-red-200',
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-['Playfair_Display'] text-3xl font-bold text-[#0F172A]">
            RAG Reports
          </h1>
          <p className="text-[#64748B] mt-1">{reports.length} total reports</p>
        </div>
        <Button 
          onClick={onRefresh}
          variant="outline"
          className="border-[#E2E8F0]"
          data-testid="refresh-reports-btn"
        >
          <TrendingUp className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="flex gap-2">
          {['all', 'green', 'amber', 'red'].map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === status
                  ? 'bg-[#0F172A] text-white'
                  : 'bg-white text-[#64748B] border border-[#E2E8F0] hover:border-[#0F172A]'
              }`}
              data-testid={`filter-${status}`}
            >
              {status === 'all' ? 'All' : status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>
        <select
          value={subjectFilter}
          onChange={(e) => setSubjectFilter(e.target.value)}
          className="px-4 py-2 rounded-lg text-sm border border-[#E2E8F0] bg-white text-[#0F172A]"
          data-testid="subject-filter"
        >
          <option value="all">All Subjects</option>
          {subjects.map(subject => (
            <option key={subject} value={subject}>{subject}</option>
          ))}
        </select>
      </div>

      {/* Reports Grid */}
      {filteredReports.length > 0 ? (
        <div className="grid md:grid-cols-2 gap-4">
          {filteredReports.map((report) => (
            <div 
              key={report.report_id} 
              className={`bg-white rounded-xl border p-6 ${
                report.rag_status === 'green' ? 'border-green-200' :
                report.rag_status === 'amber' ? 'border-amber-200' :
                'border-red-200'
              }`}
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-semibold text-[#0F172A]">{report.test_name}</h3>
                  <p className="text-sm text-[#64748B]">{report.subject}</p>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium border ${statusColors[report.rag_status]}`}>
                  {report.rag_status.toUpperCase()}
                </span>
              </div>
              
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-[#64748B]">
                  <Calendar className="w-4 h-4" />
                  <span>{report.test_date}</span>
                </div>
                {report.score && (
                  <p><span className="text-[#64748B]">Score:</span> <span className="font-medium text-[#0F172A]">{report.score}</span></p>
                )}
                {report.percentage && (
                  <p><span className="text-[#64748B]">Percentage:</span> <span className="font-medium text-[#0F172A]">{report.percentage}%</span></p>
                )}
                {report.areas_to_improve && (
                  <p className="mt-2"><span className="text-[#64748B]">Areas to improve:</span> <span className="text-[#0F172A]">{report.areas_to_improve}</span></p>
                )}
              </div>

              {report.image_url && (
                <a 
                  href={report.image_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="mt-4 inline-flex items-center gap-2 text-sm text-[#C5A059] hover:underline"
                >
                  <FileText className="w-4 h-4" />
                  View Report Image
                </a>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-[#E2E8F0] p-12 text-center">
          <FileText className="w-12 h-12 text-[#C5A059]/40 mx-auto mb-4" />
          <h3 className="font-semibold text-[#0F172A] mb-2">No Reports Found</h3>
          <p className="text-[#64748B]">
            {filter !== 'all' || subjectFilter !== 'all'
              ? 'Try adjusting your filters'
              : 'Upload your first RAG report to get started'
            }
          </p>
        </div>
      )}
    </div>
  );
}

// ============ UPLOAD TAB ============
function UploadTab({ onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [error, setError] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [imagePublicId, setImagePublicId] = useState('');
  const [successData, setSuccessData] = useState(null); // For showing recommendations
  
  const [formData, setFormData] = useState({
    subject: '',
    test_name: '',
    test_date: '',
    score: '',
    percentage: '',
    rag_status: '',
    topics_tested: '',
    areas_to_improve: '',
    teacher_comments: '',
  });

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      setError('File size must be less than 10MB');
      return;
    }

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      setError('Please upload an image (JPEG, PNG, WebP) or PDF');
      return;
    }

    setUploadingImage(true);
    setError('');

    try {
      // Get signature from backend
      const resourceType = file.type === 'application/pdf' ? 'raw' : 'image';
      const sigRes = await fetch(
        `${API_URL}/api/dashboard/cloudinary/signature?resource_type=${resourceType}`,
        { credentials: 'include' }
      );

      if (!sigRes.ok) {
        throw new Error('Failed to get upload signature');
      }

      const sig = await sigRes.json();

      // Upload to Cloudinary
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      formDataUpload.append('api_key', sig.api_key);
      formDataUpload.append('timestamp', sig.timestamp);
      formDataUpload.append('signature', sig.signature);
      formDataUpload.append('folder', sig.folder);

      const uploadRes = await fetch(
        `https://api.cloudinary.com/v1_1/${sig.cloud_name}/${resourceType}/upload`,
        { method: 'POST', body: formDataUpload }
      );

      if (!uploadRes.ok) {
        throw new Error('Failed to upload image');
      }

      const uploadData = await uploadRes.json();
      setImageUrl(uploadData.secure_url);
      setImagePublicId(uploadData.public_id);
    } catch (err) {
      setError(err.message || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validate required fields
    if (!formData.subject || !formData.test_name || !formData.test_date || !formData.rag_status) {
      setError('Please fill in all required fields');
      setLoading(false);
      return;
    }

    try {
      const reportData = {
        ...formData,
        percentage: formData.percentage ? parseFloat(formData.percentage) : null,
        topics_tested: formData.topics_tested 
          ? formData.topics_tested.split(',').map(t => t.trim()).filter(Boolean)
          : [],
        image_url: imageUrl || null,
        image_public_id: imagePublicId || null,
      };

      const res = await fetch(`${API_URL}/api/dashboard/rag-reports`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(reportData),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.detail || 'Failed to create report');
      }

      // Check if there are video recommendations (for RED/AMBER reports) or new badges
      if ((data.video_recommendations && data.video_recommendations.length > 0) || data.badges?.new_badge) {
        setSuccessData({
          report: data.report,
          recommendations: data.video_recommendations || [],
          tip: data.quick_win_tip,
          newBadge: data.badges?.new_badge,
          totalBadges: data.badges?.earned_count || 0
        });
      } else {
        onSuccess();
      }
    } catch (err) {
      setError(err.message || 'Failed to create report');
    } finally {
      setLoading(false);
    }
  };

  // Show success screen with video recommendations and/or new badge
  if (successData) {
    const hasRecommendations = successData.recommendations && successData.recommendations.length > 0;
    const hasNewBadge = successData.newBadge;
    
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-xl border border-[#E2E8F0] p-8 text-center">
          {/* New Badge Celebration */}
          {hasNewBadge && (
            <div className="mb-6 p-6 bg-gradient-to-br from-[#C5A059]/20 to-[#C5A059]/5 rounded-xl border-2 border-[#C5A059]">
              <div className="text-5xl mb-3 animate-bounce">{successData.newBadge.emoji}</div>
              <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#0F172A] mb-1">
                New Badge Earned!
              </h2>
              <h3 className="text-xl font-semibold mb-2" style={{ color: successData.newBadge.color }}>
                {successData.newBadge.name}
              </h3>
              <p className="text-[#64748B]">{successData.newBadge.description}</p>
              <p className="text-sm text-[#94A3B8] mt-2">
                You now have {successData.totalBadges} badge{successData.totalBadges !== 1 ? 's' : ''}!
              </p>
            </div>
          )}

          {!hasNewBadge && (
            <>
              <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${
                successData.report.rag_status === 'red' ? 'bg-red-100' : 
                successData.report.rag_status === 'amber' ? 'bg-amber-100' : 'bg-green-100'
              }`}>
                <CheckCircle className={`w-8 h-8 ${
                  successData.report.rag_status === 'red' ? 'text-red-500' : 
                  successData.report.rag_status === 'amber' ? 'text-amber-500' : 'text-green-500'
                }`} />
              </div>
              <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#0F172A] mb-2">
                Report Saved!
              </h2>
            </>
          )}
          
          <p className="text-[#64748B] mb-6">
            {successData.report.test_name} - {successData.report.subject}
          </p>

          {/* Quick Win Section (only for RED/AMBER) */}
          {hasRecommendations && (
            <div className="bg-gradient-to-br from-[#0F172A] to-[#1E293B] rounded-xl p-6 text-left mb-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-[#C5A059]/20 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-[#C5A059]" />
                </div>
                <div>
                  <h3 className="text-white font-semibold">Quick Win Recommendations</h3>
                  <p className="text-white/60 text-sm">{successData.tip}</p>
                </div>
              </div>
              
              <div className="space-y-3">
                {successData.recommendations.map((video, idx) => (
                  <div 
                    key={idx}
                    className="bg-white/10 rounded-lg p-4 flex items-center gap-4 hover:bg-white/15 transition-colors cursor-pointer"
                    onClick={() => {
                      window.open('/#video-lessons', '_blank');
                    }}
                  >
                    <div className="w-12 h-12 bg-[#C5A059]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <BookOpen className="w-6 h-6 text-[#C5A059]" />
                    </div>
                    <div>
                      <p className="text-white font-medium">{video.title}</p>
                      <p className="text-white/60 text-sm">{video.category}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-4">
            <Button
              onClick={() => {
                setSuccessData(null);
                setFormData({
                  subject: '', test_name: '', test_date: '', score: '', percentage: '',
                  rag_status: '', topics_tested: '', areas_to_improve: '', teacher_comments: ''
                });
                setImageUrl('');
                setImagePublicId('');
              }}
              variant="outline"
              className="flex-1 border-[#E2E8F0]"
            >
              Upload Another
            </Button>
            <Button
              onClick={onSuccess}
              className="flex-1 bg-[#C5A059] hover:bg-[#B8934E] text-white"
            >
              View All Reports
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-8">
        <h1 className="font-['Playfair_Display'] text-3xl font-bold text-[#0F172A]">
          Upload RAG Report
        </h1>
        <p className="text-[#64748B] mt-2">
          Record your school assessment results to track your progress.
        </p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-[#E2E8F0] p-6 space-y-6">
        {/* Subject & Test Name */}
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-[#0F172A] mb-2">
              Subject <span className="text-red-500">*</span>
            </label>
            <select
              name="subject"
              value={formData.subject}
              onChange={handleInputChange}
              required
              className="w-full h-11 px-3 rounded-lg border border-[#E2E8F0] focus:border-[#C5A059] focus:ring-[#C5A059]"
              data-testid="input-subject"
            >
              <option value="">Select subject</option>
              <option value="Maths">Maths</option>
              <option value="Biology">Biology</option>
              <option value="Chemistry">Chemistry</option>
              <option value="Physics">Physics</option>
              <option value="English">English</option>
              <option value="Other">Other</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-[#0F172A] mb-2">
              Test Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="test_name"
              value={formData.test_name}
              onChange={handleInputChange}
              required
              placeholder="e.g., Unit 3 Assessment"
              className="w-full h-11 px-3 rounded-lg border border-[#E2E8F0] focus:border-[#C5A059] focus:ring-[#C5A059]"
              data-testid="input-test-name"
            />
          </div>
        </div>

        {/* Date & RAG Status */}
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-[#0F172A] mb-2">
              Test Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              name="test_date"
              value={formData.test_date}
              onChange={handleInputChange}
              required
              className="w-full h-11 px-3 rounded-lg border border-[#E2E8F0] focus:border-[#C5A059] focus:ring-[#C5A059]"
              data-testid="input-test-date"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#0F172A] mb-2">
              RAG Status <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-2">
              {['red', 'amber', 'green'].map((status) => (
                <button
                  key={status}
                  type="button"
                  onClick={() => setFormData({ ...formData, rag_status: status })}
                  className={`flex-1 h-11 rounded-lg font-medium transition-all ${
                    formData.rag_status === status
                      ? status === 'red' ? 'bg-red-500 text-white' :
                        status === 'amber' ? 'bg-amber-500 text-white' :
                        'bg-green-500 text-white'
                      : 'bg-[#F1F5F9] text-[#64748B] hover:bg-[#E2E8F0]'
                  }`}
                  data-testid={`rag-${status}`}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Score & Percentage */}
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-[#0F172A] mb-2">
              Score (Optional)
            </label>
            <input
              type="text"
              name="score"
              value={formData.score}
              onChange={handleInputChange}
              placeholder="e.g., 45/60"
              className="w-full h-11 px-3 rounded-lg border border-[#E2E8F0] focus:border-[#C5A059] focus:ring-[#C5A059]"
              data-testid="input-score"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#0F172A] mb-2">
              Percentage (Optional)
            </label>
            <input
              type="number"
              name="percentage"
              value={formData.percentage}
              onChange={handleInputChange}
              min="0"
              max="100"
              step="0.1"
              placeholder="e.g., 75"
              className="w-full h-11 px-3 rounded-lg border border-[#E2E8F0] focus:border-[#C5A059] focus:ring-[#C5A059]"
              data-testid="input-percentage"
            />
          </div>
        </div>

        {/* Topics Tested */}
        <div>
          <label className="block text-sm font-medium text-[#0F172A] mb-2">
            Topics Tested (Optional)
          </label>
          <input
            type="text"
            name="topics_tested"
            value={formData.topics_tested}
            onChange={handleInputChange}
            placeholder="e.g., Algebra, Quadratics, Graphs (comma separated)"
            className="w-full h-11 px-3 rounded-lg border border-[#E2E8F0] focus:border-[#C5A059] focus:ring-[#C5A059]"
            data-testid="input-topics"
          />
        </div>

        {/* Areas to Improve */}
        <div>
          <label className="block text-sm font-medium text-[#0F172A] mb-2">
            Areas to Improve (Optional)
          </label>
          <textarea
            name="areas_to_improve"
            value={formData.areas_to_improve}
            onChange={handleInputChange}
            rows={3}
            placeholder="What topics need more work?"
            className="w-full px-3 py-2 rounded-lg border border-[#E2E8F0] focus:border-[#C5A059] focus:ring-[#C5A059] resize-none"
            data-testid="input-areas-improve"
          />
        </div>

        {/* Teacher Comments */}
        <div>
          <label className="block text-sm font-medium text-[#0F172A] mb-2">
            Teacher Comments (Optional)
          </label>
          <textarea
            name="teacher_comments"
            value={formData.teacher_comments}
            onChange={handleInputChange}
            rows={3}
            placeholder="Any feedback from your teacher?"
            className="w-full px-3 py-2 rounded-lg border border-[#E2E8F0] focus:border-[#C5A059] focus:ring-[#C5A059] resize-none"
            data-testid="input-teacher-comments"
          />
        </div>

        {/* Image Upload */}
        <div>
          <label className="block text-sm font-medium text-[#0F172A] mb-2">
            Report Screenshot (Optional)
          </label>
          <div className="border-2 border-dashed border-[#E2E8F0] rounded-lg p-6 text-center">
            {imageUrl ? (
              <div className="space-y-3">
                <CheckCircle className="w-10 h-10 text-green-500 mx-auto" />
                <p className="text-sm text-green-600 font-medium">Image uploaded successfully!</p>
                <a 
                  href={imageUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-[#C5A059] hover:underline"
                >
                  View uploaded image
                </a>
                <button
                  type="button"
                  onClick={() => {
                    setImageUrl('');
                    setImagePublicId('');
                  }}
                  className="block mx-auto text-sm text-red-500 hover:underline"
                >
                  Remove
                </button>
              </div>
            ) : uploadingImage ? (
              <div className="space-y-3">
                <Loader2 className="w-10 h-10 text-[#C5A059] animate-spin mx-auto" />
                <p className="text-sm text-[#64748B]">Uploading...</p>
              </div>
            ) : (
              <div className="space-y-3">
                <Upload className="w-10 h-10 text-[#C5A059]/60 mx-auto" />
                <p className="text-sm text-[#64748B]">
                  Upload a screenshot of your report
                </p>
                <label className="inline-block">
                  <span className="px-4 py-2 bg-[#F1F5F9] text-[#0F172A] rounded-lg text-sm font-medium cursor-pointer hover:bg-[#E2E8F0] transition-colors">
                    Choose File
                  </span>
                  <input
                    type="file"
                    accept="image/*,.pdf"
                    onChange={handleImageUpload}
                    className="hidden"
                    data-testid="input-image"
                  />
                </label>
                <p className="text-xs text-[#94A3B8]">Max 10MB (JPEG, PNG, WebP, PDF)</p>
              </div>
            )}
          </div>
        </div>

        {/* Submit Button */}
        <Button
          type="submit"
          disabled={loading || uploadingImage}
          className="w-full h-12 bg-[#C5A059] hover:bg-[#B8934E] text-white font-medium"
          data-testid="submit-report-btn"
        >
          {loading ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 className="w-5 h-5 animate-spin" />
              Saving Report...
            </span>
          ) : (
            'Save Report'
          )}
        </Button>
      </form>
    </div>
  );
}

// ============ PROFILE TAB ============
function ProfileTab({ user, linkedStudent, onRefresh }) {
  const { updateProfile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    name: user?.name || '',
    phone: user?.phone || '',
    school: user?.school || '',
    year_group: user?.year_group || '',
    exam_board: user?.exam_board || '',
    tier: user?.tier || '',
    student_email: user?.student_email || '',
  });

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    const result = await updateProfile(formData);
    
    if (result.success) {
      setSuccess('Profile updated successfully!');
      onRefresh();
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  const isStudent = user?.role === 'student';
  const isParent = user?.role === 'parent';

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-8">
        <h1 className="font-['Playfair_Display'] text-3xl font-bold text-[#0F172A]">
          My Profile
        </h1>
        <p className="text-[#64748B] mt-2">
          Update your account information.
        </p>
      </div>

      {/* Account Info Card */}
      <div className="bg-white rounded-xl border border-[#E2E8F0] p-6 mb-6">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-16 h-16 rounded-full bg-[#C5A059]/10 flex items-center justify-center">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-16 h-16 rounded-full" />
            ) : (
              <User className="w-8 h-8 text-[#C5A059]" />
            )}
          </div>
          <div>
            <h2 className="text-xl font-semibold text-[#0F172A]">{user?.name}</h2>
            <p className="text-[#64748B]">{user?.email}</p>
            <span className={`inline-block mt-1 px-3 py-1 rounded-full text-xs font-medium ${
              isStudent ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
            }`}>
              {isStudent ? 'Student Account' : 'Parent Account'}
            </span>
          </div>
        </div>
        {user?.auth_provider === 'google' && (
          <p className="text-sm text-[#64748B] flex items-center gap-2">
            <svg className="w-4 h-4" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Signed in with Google
          </p>
        )}
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
          <p className="text-green-700 text-sm">{success}</p>
        </div>
      )}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

      {/* Edit Form */}
      <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-[#E2E8F0] p-6 space-y-6">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-[#0F172A] mb-2">Full Name</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className="w-full h-11 px-3 rounded-lg border border-[#E2E8F0] focus:border-[#C5A059] focus:ring-[#C5A059]"
              data-testid="profile-name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#0F172A] mb-2">Phone</label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              placeholder="e.g., +44 7123 456789"
              className="w-full h-11 px-3 rounded-lg border border-[#E2E8F0] focus:border-[#C5A059] focus:ring-[#C5A059]"
              data-testid="profile-phone"
            />
          </div>
        </div>

        {isStudent && (
          <>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#0F172A] mb-2">School</label>
                <input
                  type="text"
                  name="school"
                  value={formData.school}
                  onChange={handleInputChange}
                  className="w-full h-11 px-3 rounded-lg border border-[#E2E8F0] focus:border-[#C5A059] focus:ring-[#C5A059]"
                  data-testid="profile-school"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#0F172A] mb-2">Year Group</label>
                <select
                  name="year_group"
                  value={formData.year_group}
                  onChange={handleInputChange}
                  className="w-full h-11 px-3 rounded-lg border border-[#E2E8F0] focus:border-[#C5A059] focus:ring-[#C5A059] bg-white"
                  data-testid="profile-year-group"
                >
                  <option value="">Select year</option>
                  <option value="Year 7">Year 7</option>
                  <option value="Year 8">Year 8</option>
                  <option value="Year 9">Year 9</option>
                  <option value="Year 10">Year 10</option>
                  <option value="Year 11">Year 11</option>
                </select>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#0F172A] mb-2">Exam Board</label>
                <select
                  name="exam_board"
                  value={formData.exam_board}
                  onChange={handleInputChange}
                  className="w-full h-11 px-3 rounded-lg border border-[#E2E8F0] focus:border-[#C5A059] focus:ring-[#C5A059] bg-white"
                  data-testid="profile-exam-board"
                >
                  <option value="">Select board</option>
                  <option value="AQA">AQA</option>
                  <option value="Edexcel">Edexcel</option>
                  <option value="OCR">OCR</option>
                  <option value="WJEC">WJEC</option>
                  <option value="iGCSE">iGCSE</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#0F172A] mb-2">Tier</label>
                <select
                  name="tier"
                  value={formData.tier}
                  onChange={handleInputChange}
                  className="w-full h-11 px-3 rounded-lg border border-[#E2E8F0] focus:border-[#C5A059] focus:ring-[#C5A059] bg-white"
                  data-testid="profile-tier"
                >
                  <option value="">Select tier</option>
                  <option value="Foundation">Foundation</option>
                  <option value="Higher">Higher</option>
                </select>
              </div>
            </div>
          </>
        )}

        {isParent && (
          <div>
            <label className="block text-sm font-medium text-[#0F172A] mb-2">
              Linked Student Email
            </label>
            <p className="text-xs text-[#64748B] mb-2">
              Enter your child's registered email to view their progress.
            </p>
            <input
              type="email"
              name="student_email"
              value={formData.student_email}
              onChange={handleInputChange}
              placeholder="student@example.com"
              className="w-full h-11 px-3 rounded-lg border border-[#E2E8F0] focus:border-[#C5A059] focus:ring-[#C5A059]"
              data-testid="profile-student-email"
            />
            {linkedStudent && (
              <p className="mt-2 text-sm text-green-600 flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Linked to: {linkedStudent.name}
              </p>
            )}
          </div>
        )}

        <Button
          type="submit"
          disabled={loading}
          className="w-full h-12 bg-[#0F172A] hover:bg-[#1E293B] text-white font-medium"
          data-testid="save-profile-btn"
        >
          {loading ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 className="w-5 h-5 animate-spin" />
              Saving...
            </span>
          ) : (
            'Save Changes'
          )}
        </Button>
      </form>
    </div>
  );
}


// ============ LEADERBOARD TAB ============
function LeaderboardTab({ currentUserId }) {
  const [loading, setLoading] = useState(true);
  const [leaderboard, setLeaderboard] = useState([]);
  const [totalStudents, setTotalStudents] = useState(0);
  const [currentUserRank, setCurrentUserRank] = useState(null);

  useEffect(() => {
    fetchLeaderboard();
  }, []);

  const fetchLeaderboard = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/dashboard/leaderboard`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setLeaderboard(data.leaderboard);
        setTotalStudents(data.total_students);
        setCurrentUserRank(data.current_user_rank);
      }
    } catch (err) {
      console.error('Error fetching leaderboard:', err);
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown className="w-6 h-6 text-yellow-500" />;
    if (rank === 2) return <Medal className="w-6 h-6 text-gray-400" />;
    if (rank === 3) return <Medal className="w-6 h-6 text-amber-600" />;
    return <span className="w-6 h-6 flex items-center justify-center text-[#64748B] font-bold">{rank}</span>;
  };

  const getRankBg = (rank, isCurrentUser) => {
    if (isCurrentUser) return 'bg-[#C5A059]/10 border-[#C5A059]';
    if (rank === 1) return 'bg-yellow-50 border-yellow-200';
    if (rank === 2) return 'bg-gray-50 border-gray-200';
    if (rank === 3) return 'bg-amber-50 border-amber-200';
    return 'bg-white border-[#E2E8F0]';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 text-[#C5A059] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-['Playfair_Display'] text-3xl font-bold text-[#0F172A]">
            Leaderboard
          </h1>
          <p className="text-[#64748B] mt-1">
            {totalStudents} students competing • Top performers by badges & green reports
          </p>
        </div>
        {currentUserRank && (
          <div className="bg-[#0F172A] text-white px-6 py-3 rounded-xl text-center">
            <p className="text-white/60 text-sm">Your Rank</p>
            <p className="text-2xl font-bold text-[#C5A059]">#{currentUserRank}</p>
          </div>
        )}
      </div>

      {/* Scoring Info */}
      <div className="bg-gradient-to-r from-[#0F172A] to-[#1E293B] rounded-xl p-6">
        <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
          <Trophy className="w-5 h-5 text-[#C5A059]" />
          How Points Are Calculated
        </h3>
        <div className="grid grid-cols-3 gap-4 text-center">
          <div className="bg-white/10 rounded-lg p-3">
            <div className="text-2xl mb-1">🟢</div>
            <p className="text-white font-bold">+10 pts</p>
            <p className="text-white/60 text-xs">Per Green Report</p>
          </div>
          <div className="bg-white/10 rounded-lg p-3">
            <div className="text-2xl mb-1">🏆</div>
            <p className="text-white font-bold">+25 pts</p>
            <p className="text-white/60 text-xs">Per Badge Earned</p>
          </div>
          <div className="bg-white/10 rounded-lg p-3">
            <div className="text-2xl mb-1">📊</div>
            <p className="text-white font-bold">+2 pts</p>
            <p className="text-white/60 text-xs">Per Report</p>
          </div>
        </div>
      </div>

      {/* Leaderboard List */}
      <div className="space-y-3">
        {leaderboard.map((entry) => (
          <div
            key={entry.user_id}
            className={`rounded-xl border-2 p-4 transition-all ${getRankBg(entry.rank, entry.is_current_user)}`}
          >
            <div className="flex items-center gap-4">
              {/* Rank */}
              <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center shadow-sm">
                {getRankIcon(entry.rank)}
              </div>

              {/* User Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className={`font-semibold truncate ${entry.is_current_user ? 'text-[#C5A059]' : 'text-[#0F172A]'}`}>
                    {entry.name}
                    {entry.is_current_user && <span className="text-xs ml-2">(You)</span>}
                  </h3>
                </div>
                <div className="flex items-center gap-3 text-sm text-[#64748B]">
                  {entry.school && <span>{entry.school}</span>}
                  {entry.year_group && <span>• {entry.year_group}</span>}
                </div>
                {/* Badges Preview */}
                {entry.badges && entry.badges.length > 0 && (
                  <div className="flex gap-1 mt-2">
                    {entry.badges.map((badge, idx) => (
                      <span 
                        key={idx} 
                        className="text-lg" 
                        title={badge.name}
                      >
                        {badge.emoji}
                      </span>
                    ))}
                    {entry.stats.badge_count > 3 && (
                      <span className="text-xs text-[#64748B] self-center">
                        +{entry.stats.badge_count - 3} more
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Stats */}
              <div className="text-right">
                <div className="text-2xl font-bold text-[#0F172A]">{entry.score}</div>
                <div className="text-xs text-[#64748B]">points</div>
              </div>

              {/* Mini Stats */}
              <div className="hidden sm:flex gap-3">
                <div className="text-center px-3 py-2 bg-green-50 rounded-lg">
                  <div className="text-lg font-bold text-green-600">{entry.stats.green_count}</div>
                  <div className="text-xs text-green-600">Green</div>
                </div>
                <div className="text-center px-3 py-2 bg-[#F1F5F9] rounded-lg">
                  <div className="text-lg font-bold text-[#0F172A]">{entry.stats.badge_count}</div>
                  <div className="text-xs text-[#64748B]">Badges</div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {leaderboard.length === 0 && (
        <div className="bg-white rounded-xl border border-[#E2E8F0] p-12 text-center">
          <Trophy className="w-12 h-12 text-[#C5A059]/40 mx-auto mb-4" />
          <h3 className="font-semibold text-[#0F172A] mb-2">No Rankings Yet</h3>
          <p className="text-[#64748B]">
            Be the first to upload reports and climb the leaderboard!
          </p>
        </div>
      )}

      {/* Motivational Footer */}
      <div className="text-center py-6">
        <p className="text-[#64748B] text-sm">
          💡 <strong>Pro tip:</strong> Upload more reports and earn badges to climb the rankings!
        </p>
      </div>
    </div>
  );
}
